import { getStageFromUrl } from '@insurancemenu/imenu-components-lib/dist/helpers';
import { Stage } from '@insurancemenu/imenu-components-lib/dist/types';
import { logoutUserRequest, setAuthUserType, setUserOrgAffiliation } from 'api/aaaService';
import axios from 'axios';
import Cookies from 'js-cookie';
import { makeAutoObservable, runInAction, toJS } from 'mobx';
import { RootStore } from 'store/rootStore';
import { Agency, Sponsor, SubAgency, UserRoleTypes } from 'types/aaaService';

export default class RoleTypeAndAgencyStore {
  rootStore: RootStore;
  loading = false;
  /**
   * List of all available user role types. If only 1 role type is present, the role types switcher will be hidden.
   */
  roleTypes: UserRoleTypes[] = [];
  /**
   * List of all sponsors, agencies, and subagencies.
   */
  allAgencies: Agency[] = [];
  /**
   * Agencies that are filtered by the search value to show in the SearchAgency component list.
   */
  filteredAgencies: Agency[] = [];
  /**
   * List of all available sponsors from the allAgencies collection.
   */
  sponsors: Sponsor[] = [];
  /**
   * Selected Role type if there are multiple types; otherwise, the component with the role type switcher will be hidden.
   */
  selectedRoleType: UserRoleTypes | null = null;
  /**
   * Selected sponsor in the change agency form.
   */
  selectedSponsor: Sponsor | null = null;
  /**
   * Selected agency in the change agency form.
   */
  selectedAgency: Agency | null = null;
  /**
   * Selected subagency in the change agency form.
   */
  selectedSubAgency: SubAgency | null = null;

  /**
   * Flag indicating whether there is only one main agency with multiple subagencies.
   * When set to true, it changes the component view to optimize the user experience:
   * - The agency selection is hidden, and the subagency selection is visually displayed as the agency select.
   * This design minimizes confusion, as users won't realize they are selecting subagencies directly.
   */
  isSingleAgencyWithSubagencies = false;

  // Setters
  private setLoading(status: boolean): void {
    this.loading = status;
  }

  private setSingleAgencyWithSubagencies(status: boolean): void {
    this.isSingleAgencyWithSubagencies = status;
  }

  public setSelectedRoleType(roleType: UserRoleTypes | null): void {
    this.selectedRoleType = roleType;
    this.resetAgencyFormSelection();
    this.setDefaultFormValues();
  }

  public setSelectedSponsor(sponsor: Sponsor | null): void {
    this.selectedSponsor = sponsor;
    // Reset agency and subagency
    this.setSelectedAgency(null);
    this.setSelectedSubAgency(null);
  }

  public setSelectedAgency(agency: Agency | null): void {
    this.selectedAgency = agency;
    this.setSelectedSubAgency(null);
  }

  public setSelectedSubAgency(subAgency: SubAgency | null): void {
    this.selectedSubAgency = subAgency;
  }

  public setFilteredAgencies(agencies: Agency[]): void {
    this.filteredAgencies = agencies;
  }

  private resetAgencyFormSelection(): void {
    this.setSelectedSponsor(null);
    this.setSelectedAgency(null);
    this.setSelectedSubAgency(null);
  }

  private setDefaultFormValues(): void {
    if (this.sponsors.length === 1 && !this.selectedSponsor) {
      this.setSelectedSponsor(this.sponsors[0]);
    }
    if (this.availableAgencies.length === 1 && !this.selectedAgency) {
      this.setSelectedAgency(this.availableAgencies[0]);
    }
    if (this.availableSubAgencies.length === 1 && !this.selectedSubAgency) {
      this.setSelectedSubAgency(this.availableSubAgencies[0]);
    }
  }

  // Getters
  public get availableAgencies(): Agency[] {
    if (this.sponsors.length <= 1) return this.allAgencies;

    return this.selectedSponsor === null
      ? []
      : this.allAgencies.filter((agency) =>
          agency.sponsors.some((agencySponsor) => agencySponsor.id === this.selectedSponsor?.id),
        );
  }

  public get availableSubAgencies(): SubAgency[] {
    if (!this.selectedAgency) return [];
    return this.selectedAgency.subAgencies;
  }

  /**
   * Determines if agency selection is available for the current user role type.
   * For agents, agency selection is available if there is more than one sponsor,
   * more than one main agency, or more than one sub agency.
   */
  public get isMultiAgencySelectionAvailable(): boolean {
    return this.sponsors.length > 1 || this.availableAgencies.length > 1 || this.availableSubAgencies.length > 1;
  }

  public get isAgencyFormValid(): boolean {
    if (this.availableSubAgencies.length > 0) {
      return Boolean(this.selectedSponsor && this.selectedAgency && this.selectedSubAgency);
    }
    return Boolean(this.selectedSponsor && this.selectedAgency);
  }

  // eslint-disable-next-line class-methods-use-this
  private get currentStage(): Stage {
    return getStageFromUrl();
  }

  // Methods for handling backend requests

  /**
   * Fetches user role types and agency information.
   */
  public async getInitialDataForLoggedInUser(): Promise<void> {
    this.setLoading(true);
    await Promise.all([this.getUserRoleTypes(), this.getAllAgencies()]);
    this.setLoading(false);
  }

  private async getUserRoleTypes(): Promise<void> {
    const userRoleTypes = await this.rootStore.aaaStore.getAuthUserTypes();
    runInAction(() => {
      this.roleTypes = userRoleTypes;
      // Make single role type selected by default
      if (userRoleTypes.length === 1) this.setSelectedRoleType(userRoleTypes[0]);
    });
  }

  /**
   * Retrieves organizational affiliation information and populates the list of agencies.
   * Extracts sponsors from the affiliation data.
   */
  private async getAllAgencies(): Promise<void> {
    const allAgencies = await this.rootStore.aaaStore.getOrgAffiliation();
    runInAction(() => {
      this.allAgencies = allAgencies;
      // Extract sponsors from the affiliation and update the available sponsors
      this.setAvailableSponsors(allAgencies);
      const isAgencySingle = allAgencies.length === 1;

      // Make single agency selected by default
      if (isAgencySingle) {
        const singleAgency = allAgencies[0];
        this.setSelectedAgency(singleAgency);

        if (singleAgency.subAgencies.length === 1) {
          this.setSelectedSubAgency(singleAgency.subAgencies[0]);
        }

        if (singleAgency.subAgencies.length > 1) {
          this.setSingleAgencyWithSubagencies(true);
        }
      }
    });
  }

  private handleAnnouncements = async (sponsorId: string): Promise<void> => {
    const allAnnouncements = await this.rootStore.announcementStore.getAllAnnouncements();
    runInAction(() => {
      // Get the stored readAnnouncementIds from cookies
      const readAnnouncementIdsJSON = Cookies.get(`${sponsorId}_readAnnouncements_${this.currentStage}`);
      const readAnnouncementIds = readAnnouncementIdsJSON ? (JSON.parse(readAnnouncementIdsJSON) as string[]) : [];

      const notReadAnnouncements = allAnnouncements.map(({ id }) => !readAnnouncementIds.includes(id as string));

      if (sponsorId && notReadAnnouncements.length > 0) {
        Cookies.set(`${sponsorId}_wasAnnouncementModalShown_${this.currentStage}`, 'false', {
          expires: 365,
          domain: process.env.REACT_APP_DOMAIN_NAME_URL,
        });
      }
    });
  };

  public saveAndContinueWithUserSelection = async (): Promise<void> => {
    if (!this.selectedRoleType) return;

    this.setLoading(true);
    if (this.roleTypes.length > 1) {
      try {
        await this.updateUserTypeIfNeeded();
      } catch {
        return;
      }
    }

    await this.handleAgentFlowIfNeeded();
    this.setLoginCookie();

    // Redirect to the Broker Portal (or redirectURL path if present).
    window.location.href = this.buildRedirectUrl();
    // Avoid changing the loading status to prevent a fast spinner disappearance
    // and displaying the active form before the redirect is completed.
  };

  private async updateUserTypeIfNeeded(): Promise<void> {
    const res = await this.rootStore.requestProcessor.processRequest(setAuthUserType, {
      type: this.selectedRoleType,
    });
    if (!res || res.status !== 200) {
      this.setLoading(false);
      throw new Error('Set auth user type failed');
    }
  }

  private async handleAgentFlowIfNeeded(): Promise<void> {
    if (this.selectedRoleType !== 'AGENT') return;
    if (!this.selectedSponsor || !this.selectedAgency) return;

    // Set org affiliation
    await this.rootStore.requestProcessor.processRequest(setUserOrgAffiliation, {
      sponsor: this.selectedSponsor,
      agency: {
        id: this.selectedAgency.id,
        name: this.selectedAgency.name,
      },
      subAgency: this.selectedSubAgency ? { id: this.selectedSubAgency.id, name: this.selectedSubAgency.name } : {},
    });

    // Handle announcements
    await this.handleAnnouncements(this.selectedSponsor.id);

    // Set cookie for multi-agency selection option
    // Determine whether to display the "Change Agency" button in the user dropdown options.
    // The button is shown when the user has the role type 'AGENT' and there are multiple sponsors,
    // main agencies, or subagencies
    Cookies.set(`showChangeAgencyOption_${this.currentStage}`, this.isMultiAgencySelectionAvailable.toString(), {
      expires: 365,
      domain: process.env.REACT_APP_DOMAIN_NAME_URL,
    });
  }

  // eslint-disable-next-line class-methods-use-this
  private setLoginCookie(): void {
    Cookies.set('login_time', Date.now().toString(), {
      expires: 1,
      domain: process.env.REACT_APP_DOMAIN_NAME_URL,
    });
  }

  // eslint-disable-next-line class-methods-use-this
  private buildRedirectUrl(): string {
    const redirectPath = new URLSearchParams(window.location.search).get('redirectURL');
    const baseUrl = `https://${process.env.REACT_APP_BROKER_PORTAL_BASE_URL}`;

    return redirectPath ? `${baseUrl}/${redirectPath.replace(/^\//, '')}` : `${baseUrl}/`;
  }

  public async sendLogoutUserRequest(): Promise<void> {
    this.setLoading(true);

    try {
      await logoutUserRequest();
      this.setLoading(false);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status !== 401) {
        this.rootStore.notificationStore.setNotification({
          type: 'error',
          title: 'Unexpected server error!',
          msg: `${error as unknown as string}`,
        });
        this.setLoading(false);
      }
    }
    this.rootStore.loginSessionStore.logOutUser();
    this.resetInitialData();
    // Bust the other tabs' sessions
    Cookies.set('login_time', Date.now().toString(), {
      expires: 1,
      domain: process.env.REACT_APP_DOMAIN_NAME_URL,
    });
  }

  // Helpers

  /**
   * Sets the available sponsors by filtering out duplicates and sorting by name.
   */
  private setAvailableSponsors = (allAgencies: Agency[]): void => {
    // Get all sponsors from the agencies
    const allSponsors: Sponsor[] = allAgencies.flatMap((agency) => agency.sponsors);

    // Use a Set to efficiently keep track of unique sponsor ids
    const uniqueSponsorIds: Set<string> = new Set();

    // Filter out duplicates based on sponsor id and add to the Set
    const uniqueSponsors = allSponsors
      .filter(({ id }) => !uniqueSponsorIds.has(id) && uniqueSponsorIds.add(id))
      .sort((a, b) => a.name.localeCompare(b.name));
    this.sponsors = uniqueSponsors;
    if (uniqueSponsors.length === 1) this.setSelectedSponsor(uniqueSponsors[0]);
  };

  /**
   * Searches for agencies based on the provided search value within the available agency collection.
   * Updates the 'filteredAgencies' array with matching agencies.
   * */
  public searchAgencyByName(searchValue: string): void {
    const convertedAgencies = this.availableAgencies.map((agency) => toJS(agency)).slice();
    this.filteredAgencies = convertedAgencies.filter((agency) =>
      agency.name.toLowerCase().includes(searchValue.toLowerCase()),
    );
  }

  public resetInitialData(): void {
    this.allAgencies = [];
    this.filteredAgencies = [];
    this.sponsors = [];
    this.selectedRoleType = null;
    this.resetAgencyFormSelection();
    this.isSingleAgencyWithSubagencies = false;
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    makeAutoObservable(this, {}, { autoBind: true });
  }
}
